import type { Point, PointArray } from '../types/contour-definition-array-type';

import { getPolygonAreaAndCentroid } from './getPolygonAreaAndCentroid';
import { removeDuplicatePoints } from './removeDuplicatePoints';
import { removeLoops } from './removeLoops';

/**
 * Shrinks a closed polygon inward by `distance` (a uniform erosion).
 *
 * Each vertex is moved along the averaged inward normal of its two adjacent
 * edges. Any self-intersections introduced by the offset are removed, and the
 * largest remaining ring is returned.
 *
 * Coordinates are interpreted in a planar space (image pixels), so this is
 * robust at any scale (unlike a geodesic turf buffer).
 */
export function insetPolygon(polygon: PointArray, distance: number): PointArray {
  if (distance <= 0 || polygon.length < 4) return polygon;

  // strip closing duplicate
  let pts = polygon;
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (first.x === last.x && first.y === last.y) {
    pts = polygon.slice(0, -1);
  }
  const n = pts.length;
  if (n < 3) return polygon;

  // reference interior point for choosing the inward normal direction
  const { centroid } = getPolygonAreaAndCentroid([...pts, pts[0]]);
  let cx = centroid.x;
  let cy = centroid.y;
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) {
    cx = pts.reduce((sum, p) => sum + p.x, 0) / n;
    cy = pts.reduce((sum, p) => sum + p.y, 0) / n;
  }

  // inward unit normal per edge (i -> i+1)
  const normals: Point[] = Array.from({ length: n });
  for (let i = 0; i < n; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % n];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    let nx = -dy / len;
    let ny = dx / len;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    if ((cx - mx) * nx + (cy - my) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    normals[i] = { x: nx, y: ny };
  }

  // move each vertex inward along the averaged adjacent edge normals, using a
  // miter factor so the perpendicular offset of each edge equals `distance`
  const result: PointArray = Array.from({ length: n });
  for (let i = 0; i < n; i++) {
    const nPrev = normals[(i - 1 + n) % n];
    const nNext = normals[i];
    let mx = nPrev.x + nNext.x;
    let my = nPrev.y + nNext.y;
    const len = Math.hypot(mx, my);
    if (len < 1e-6) {
      mx = nNext.x;
      my = nNext.y;
    }
    else {
      mx /= len;
      my /= len;
    }
    // cos(half-angle) between the two edge normals; clamp to cap sharp miters
    let cos = mx * nNext.x + my * nNext.y;
    if (cos < 0.25) cos = 0.25;
    const t = distance / cos;
    result[i] = { x: pts[i].x + mx * t, y: pts[i].y + my * t };
  }

  // drop vertices that collapsed onto each other during the offset, otherwise
  // turf (removeLoops) rejects the polygon for duplicate vertices
  const deduped = removeDuplicatePoints(result);
  if (deduped.length < 3) return polygon;

  // re-close the ring
  deduped.push({ x: deduped[0].x, y: deduped[0].y });

  try {
    return removeLoops(deduped);
  }
  catch {
    return deduped;
  }
}
